import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from '../../services/toastMessage';
import AssistantPromptBox, { AssistantPromptCopy } from './AssistantPromptBox';
import { useSpeechDictation } from '../../hooks/useSpeechDictation';
import { identifyMachinery, draftMachinery, readMachineryPlate } from '../../services/MachineryAssistService';
import { IMachineryDraft, IMachineryIdentifyResult, IMachineryPlateResult } from '../../interfaces/heavyDutyMachinery/IMachineryAssist';
import { IClarifyingQuestion, IServiceRequestClarification } from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import { getCustomSpecFields } from '../../constants/machineryCustomSpecFieldsByType';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const MACHINERY_ASSISTANT_COPY: AssistantPromptCopy = {
  titleKey: TRANSLATIONS.MACHINERY_AI_TITLE,
  subtitleKey: TRANSLATIONS.MACHINERY_AI_SUBTITLE,
  doneSubtitleKey: TRANSLATIONS.MACHINERY_AI_DONE_SUBTITLE,
  placeholderKey: TRANSLATIONS.MACHINERY_AI_PLACEHOLDER,
  fillButtonKey: TRANSLATIONS.MACHINERY_AI_FILL_BUTTON,
  retryButtonKey: TRANSLATIONS.MACHINERY_AI_RETRY_BUTTON,
  continueButtonKey: TRANSLATIONS.MACHINERY_AI_CONTINUE_BUTTON,
};

/** The same floor the server refuses below -- "PC210" is a complete thing to say, so it is short on purpose. */
const MIN_DESCRIPTION_LENGTH = 4;

export interface MachineryDraftApplied {
  draft: IMachineryDraft;
  /** The category the draft was made for -- the assistant's own answer, or the one the host had already picked. */
  categoryCode: string;
  manufacturerBrand?: string;
  modelName?: string;
}

interface MachineryDraftAssistantProps {
  /**
   * What the identification plate said: make, model, chassis number and year.
   * Separate from onApplied because it arrives earlier and carries something
   * else entirely -- four facts about ONE physical machine, read off metal,
   * rather than facts about a model.
   */
  onPlateRead?: (plate: IMachineryPlateResult) => void;
  /** Whatever the host has already chosen in the wizard's own picker. Used when the assistant cannot tell what the machine is -- pressing the button again then still works. */
  currentCategory?: string;
  disabled?: boolean;
  onApplied: (applied: MachineryDraftApplied) => void;
}

/**
 * "Dime qué máquina vas a publicar" -- and then most of the data sheet fills
 * itself in.
 *
 * Publishing asks for around sixty fields and about twenty-five of them are
 * not the host's opinion: the operating weight of a PC210 is the same for
 * everybody and it is in a manual they would have to go and find. This reads a
 * sentence, works out which machine it is, and fills those in.
 *
 * **Nothing here is ever in the way.** It sits at the top of the first step as
 * one box somebody can ignore completely; every field it fills was going to be
 * typed by hand anyway and still can be; it never overwrites an answer already
 * given (see applyMachineryDraft); and when the assistant is not switched on,
 * the block removes itself rather than offering a button that cannot work. A
 * host who wants to fill in sixty fields by hand fills in sixty fields by hand
 * and never finds out this was here.
 *
 * **The price is not in it, and that is not an omission.** What something is
 * worth is the host's own position, and a suggested price is the one wrong
 * answer that costs them money the first day. Same for the location, the
 * deposit and the cancellation policy.
 *
 * Structurally a sibling of JobClarifier, which does the same job at the other
 * end of the platform -- same two calls, same skippable questions, same
 * "unavailable is an ordinary state" stance.
 */
const MachineryDraftAssistant: React.FC<MachineryDraftAssistantProps> = ({ currentCategory, disabled, onApplied, onPlateRead }) => {
  const { t, i18n } = useTranslation();
  const dictation = useSpeechDictation(i18n.language);

  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<IClarifyingQuestion[]>();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [identified, setIdentified] = useState<IMachineryIdentifyResult>();
  const [working, setWorking] = useState(false);
  // Set once the server says the assistant is not configured here. The block
  // puts itself away rather than leaving a button that cannot work.
  const [unavailable, setUnavailable] = useState(false);
  const [done, setDone] = useState(false);
  const [reading, setReading] = useState(false);

  useEffect(() => {
    if (dictation.transcript) setDescription(dictation.transcript);
  }, [dictation.transcript]);

  useEffect(() => {
    if (dictation.error) message.error(translate(t, TRANSLATIONS.DICTATE_ERROR));
  }, [dictation.error, t]);

  /** Turns one failed call into the right sentence, and decides whether the block survives it. */
  const reportFailure = (reason: 'unavailable' | 'limit' | 'error', error?: string) => {
    if (reason === 'unavailable') {
      setUnavailable(true);
      return;
    }
    if (reason === 'limit') {
      message.warning(translate(t, TRANSLATIONS.MACHINERY_AI_LIMIT_REACHED));
      return;
    }
    // The host gets the translated line; the server's own text is English and
    // written for us. It goes to the console, where it is still there when
    // somebody needs it.
    if (error) console.error('machinery assistant:', error);
    message.error(translate(t, TRANSLATIONS.MACHINERY_AI_FAILED));
  };

  /** The second call: the data sheet itself, once there is a category to draft against. */
  const buildDraft = async (
    categoryCode: string,
    identify?: IMachineryIdentifyResult,
    pairs: IServiceRequestClarification[] = [],
    said: string = description
  ) => {
    const outcome = await draftMachinery(said.trim(), categoryCode, i18n.language, {
      manufacturerBrand: identify?.manufacturerBrand,
      modelName: identify?.modelName,
      answers: pairs,
      // This client's own per-type schema. The server has no copy of it, so
      // sending it is the only way it can ask for the right keys -- and throw
      // every other one away.
      customSpecFields: getCustomSpecFields(categoryCode),
      translateKey: (key) => translate(t, key),
    });

    if (!outcome.ok) {
      reportFailure(outcome.reason, outcome.error);
      return;
    }

    onApplied({
      draft: outcome.value,
      categoryCode,
      manufacturerBrand: identify?.manufacturerBrand,
      modelName: identify?.modelName,
    });
    // Back to a plain "try again": the questions have been answered and
    // asking them a second time would look like nobody listened.
    setQuestions(undefined);
    setDone(true);
  };

  /**
   * @param said what to work from. Passed in rather than read off state
   *        because the plate chains straight into this, and a setState from
   *        the same tick would not be visible here yet.
   */
  const handleStart = async (said: string = description) => {
    if (said.trim().length < MIN_DESCRIPTION_LENGTH) {
      message.error(translate(t, TRANSLATIONS.MACHINERY_AI_DESCRIPTION_TOO_SHORT));
      return;
    }

    dictation.stop();
    setWorking(true);
    try {
      const outcome = await identifyMachinery(said.trim(), i18n.language);
      if (!outcome.ok) {
        reportFailure(outcome.reason, outcome.error);
        return;
      }

      const identify = outcome.value;
      setIdentified(identify);

      // No category from the assistant and none picked by hand: "una máquina"
      // identifies nothing. Said plainly, rather than guessed at -- the
      // wizard's own picker is right underneath, and pressing this again
      // afterwards works, because currentCategory is read fresh.
      const categoryCode = identify.categoryCode ?? currentCategory;
      if (!categoryCode) {
        message.info(translate(t, TRANSLATIONS.MACHINERY_AI_NO_CATEGORY));
        return;
      }

      if (identify.questions.length > 0) {
        setQuestions(identify.questions);
        return;
      }

      await buildDraft(categoryCode, identify, [], said);
    } finally {
      setWorking(false);
    }
  };

  /**
   * A photo of the identification plate.
   *
   * The chassis number is why this exists -- seventeen characters of mixed
   * letters and digits, the field that most often gets typed wrong and the
   * only one nobody can check later by looking at the machine. The make and
   * model come along for free, and they are exactly what the rest of the
   * assistant needs, so a read that finds them carries straight on into it:
   * one photo, and the form fills itself.
   */
  const handlePhoto = async (photo: File) => {
    setReading(true);
    try {
      const outcome = await readMachineryPlate(photo);
      if (!outcome.ok) {
        reportFailure(outcome.reason, outcome.error);
        return;
      }

      const plate = outcome.value;
      if (!plate.plateFound) {
        // Nothing failed -- they photographed something else, or too far away.
        message.info(translate(t, TRANSLATIONS.MACHINERY_AI_PLATE_NOT_FOUND));
        return;
      }

      onPlateRead?.(plate);

      const said = [plate.manufacturerBrand, plate.modelName].filter(Boolean).join(' ');
      if (said.length < MIN_DESCRIPTION_LENGTH) {
        // A plate whose make and model were not legible, but whose chassis
        // number was. That is still the hard field done, and worth saying --
        // there is simply nothing to look the specs up from.
        message.success(translate(t, TRANSLATIONS.MACHINERY_AI_PLATE_SERIAL_ONLY));
        return;
      }

      // Only when they had not written anything themselves: somebody who
      // typed "excavadora de ruedas Komatsu" and then photographed the plate
      // meant both, and the sentence they wrote is the better prompt.
      const prompt = description.trim().length >= MIN_DESCRIPTION_LENGTH ? description : said;
      if (prompt !== description) setDescription(said);

      await handleStart(prompt);
    } finally {
      setReading(false);
    }
  };

  const handleAnswered = async () => {
    const categoryCode = identified?.categoryCode ?? currentCategory;
    if (!categoryCode) return;

    const pairs: IServiceRequestClarification[] = (questions ?? [])
      .map((question) => ({ question: question.question, answer: answers[question.question] }))
      .filter((pair): pair is IServiceRequestClarification => !!pair.answer);

    setWorking(true);
    try {
      await buildDraft(categoryCode, identified, pairs);
    } finally {
      setWorking(false);
    }
  };

  if (unavailable) return null;

  const hasQuestions = !!questions && questions.length > 0;

  return (
    <AssistantPromptBox
      copy={MACHINERY_ASSISTANT_COPY}
      description={description}
      onDescriptionChange={setDescription}
      dictation={dictation}
      recognised={
        identified?.categoryName
          ? [identified.categoryName, identified.manufacturerBrand, identified.modelName].filter(Boolean).join(' · ')
          : undefined
      }
      questions={questions}
      answers={answers}
      onAnswer={(question, option) => setAnswers((previous) => ({ ...previous, [question]: option }))}
      working={working}
      done={done}
      disabled={disabled}
      onSubmit={hasQuestions ? handleAnswered : () => handleStart()}
      onPhoto={handlePhoto}
      photoWorking={reading}
      photoLabelKey={TRANSLATIONS.MACHINERY_AI_PLATE_BUTTON}
    />
  );
};

export default MachineryDraftAssistant;
